import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { activeJourneyContext } from './activeJourneyContext';
import { TFormData, TFormField } from './types';
import { unAnsweredFields } from './utils';

/**
 * This is the context for the collapsable group sections
 * rules / requirments
 * - only one group can be open at a time
 * - only one group can be completed at a time
 * - first group is always open by default
 * - if all required fields are filled, the group is marked as completed
 * - child groups are unlocked by default
 * @see AccordionSectionField.tsx
 */
export interface TGroupsFieldContext {
  openAccordion: (id: string) => void;
  closeAccordion: (fieldToClose?: string) => void;
  isOpen: (id: string) => boolean;
  groupIds?: string[];
  completed?: boolean[];
  markCompleted?: (name: string) => void;
  markUnCompleted?: (name: string) => void;
  openAccordionId: string | null;
  firstFieldId?: string;
  openMap: Map<string, GroupMapEntry>;
  isCompleted: (name: string) => boolean;
  getGroupItem: (id: string) => GroupMapEntry | GroupChildEntry | undefined;
  handleNext: (id: string, formData: TFormData, fields: TFormField[], setUnAnswered: any) => void;
}

type GroupChildEntry = {
  open: boolean;
  completed: boolean;
  unlocked?: boolean;
  parent?: string;
};

type GroupMapEntry = {
  open: boolean;
  children: Map<string, GroupChildEntry>;
  completed: boolean;
  unlocked?: boolean;
  position: number;
};

type GroupMap = Map<string, GroupMapEntry>;
// Build the group map to track open/closed groups and other states
export function buildGroupMap(sections: { fields: TFormField[] }[]): Map<string, GroupMapEntry> {
  const groupMap = new Map<string, GroupMapEntry>();

  function traverse(fields: TFormField[], parentGroupName?: string) {
    let index = 0;
    for (const field of fields) {
      if (field.type === 'group') {
        // Ensure this group is in the map
        if (!groupMap.has(field.name) && !parentGroupName) {
          groupMap.set(field.name, { open: index === 0, children: new Map(), completed: false, position: index });
          index++;
        }
        // Add this group to its parent's children list if parent exists
        if (parentGroupName) {
          const parent = groupMap.get(parentGroupName);
          if (parent) {
            parent.children.set(field.name, { open: false, completed: false, unlocked: true, parent: parentGroupName });
          }
        }
        // Recurse into this group's fields, passing current group as parent
        if (field.fields) {
          traverse(field.fields, field.name);
        }
      } else if (field.fields) {
        // If non-group field has nested fields, traverse with same parent
        traverse(field.fields, parentGroupName);
      }
    }
  }

  for (const section of sections) {
    traverse(section.fields);
  }

  return groupMap;
}

export const GroupsFieldContext = createContext<TGroupsFieldContext | undefined>(undefined);
/*
 * ----------------------------------------------
 * Main Context Provider
 * ----------------------------------------------
 */
export const GroupsFieldProvider = ({ children, firstFieldId }: { firstFieldId?: string; children: any }) => {
  const journeyContext = useContext(activeJourneyContext);
  const [openAccordionId, setOpenAccordionId] = useState<string | null>(firstFieldId || null);
  const [openMap, setOpenMap] = useState<Map<string, GroupMapEntry>>(new Map());

  useEffect(() => {
    const groups = buildGroupMap(journeyContext?.sections ?? []);
    setOpenMap(groups);
  }, [journeyContext]);

  const openAccordion = (id: string): void => {
    setOpenMap((prevMap) => {
      const updatedMap: GroupMap = new Map();

      prevMap.forEach((value, key) => {
        const isGroup = key === id;
        const isChildOfGroup = value.children.has(id);

        if (isGroup) {
          updatedMap.set(key, { ...value, open: true });
        } else if (isChildOfGroup) {
          updatedMap.set(key, {
            ...value,
            open: true,
            children: new Map(
              Array.from(value.children.entries()).map(([childKey, childValue]) => [
                childKey,
                { ...childValue, open: childKey === id },
              ]),
            ),
          });
        } else {
          updatedMap.set(key, { ...value, open: false });
        }
      });

      setOpenAccordionId(id);
      return updatedMap;
    });
  };

  const closeAccordion = (fieldToClose?: string): void => {
    const id = fieldToClose || openAccordionId;
    if (!id) return;

    setOpenMap((prevMap) => {
      const updatedMap = new Map(prevMap);

      if (updatedMap.has(id)) {
        const entry = updatedMap.get(id)!;
        updatedMap.set(id, { ...entry, open: false });
      } else {
        // check if child
        for (const [key, entry] of updatedMap.entries()) {
          if (entry.children.has(id)) {
            const updatedChildren = new Map(entry.children);
            const child = updatedChildren.get(id)!;
            updatedChildren.set(id, { ...child, open: false });
            updatedMap.set(key, { ...entry, children: updatedChildren });
            break;
          }
        }
      }

      return updatedMap;
    });
  };

  const updateGroupCompletion = (name: string, completed: boolean) => {
    setOpenMap((prevMap) => {
      const updatedMap = new Map(prevMap);

      if (updatedMap.has(name)) {
        const group = updatedMap.get(name)!;
        updatedMap.set(name, { ...group, completed });
      } else {
        // it's a child
        for (const [parentKey, group] of updatedMap) {
          if (group.children.has(name)) {
            const updatedChildren = new Map(group.children);
            const child = updatedChildren.get(name)!;
            updatedChildren.set(name, { ...child, completed });
            updatedMap.set(parentKey, { ...group, children: updatedChildren });
            break;
          }
        }
      }

      return updatedMap;
    });
  };

  const markCompleted = (name: string) => updateGroupCompletion(name, true);
  const markUnCompleted = (name: string) => updateGroupCompletion(name, false);

  const isOpen = (id: string): boolean => {
    const item = getGroupItem(id);
    return item?.open || false;
  };

  const handleNext = (id: string, formData: TFormData, fields: TFormField[], setUnAnswered: any) => {
    const hasUnAnsweredFields = unAnsweredFields(formData, fields);
    if (hasUnAnsweredFields && hasUnAnsweredFields?.length > 0) {
      setUnAnswered(hasUnAnsweredFields);
      return;
    } else {
      setUnAnswered(null);
    }

    // if all is good. open next group
    const groups = Array.from(openMap.keys());
    const currentIndex = groups.indexOf(id || '');
    const isLastGroup = currentIndex === groups.length - 1;
    if (currentIndex !== -1 && currentIndex + 1 < groups.length) {
      const nextGroup = groups[currentIndex + 1];
      markCompleted(id || '');
      openAccordion(nextGroup);
    } else {
      // if its child or last group mark it as completed and close
      const item = getGroupItem(id || '');
      if ((item && 'parent' in item) || isLastGroup) {
        const parent = getGroupItem((item as GroupChildEntry)?.parent || '') as GroupMapEntry;
        const children = Array.from((parent?.children || []).keys());
        const nextChildIndex = children.indexOf(id || '') + 1
        // if next child is out of bounds just close and mark as completed
        if(nextChildIndex >= children.length) {
          markCompleted(id || '');
          closeAccordion(id || '');
        }else {
          // open next child
          const nextChild = children[nextChildIndex];
          markCompleted(id || '');
          openAccordion(nextChild || '');
        }
      }
    }
  };

  const isCompleted = (key: string) => {
    const item = getGroupItem(key);
    if (item) {
      return item.completed;
    }

    return false;
  };

  const getGroupItem = (id: string): GroupMapEntry | GroupChildEntry | undefined => {
    // check root level
    const root = openMap.get(id);
    if (root) {
      return root;
    }

    // check child level
    for (const { children } of openMap.values()) {
      const child = children.get(id);
      if (child) {
        return child;
      }
    }
  };

  const contextValue = useMemo(
    () => ({
      openAccordion,
      closeAccordion,
      isOpen,
      markCompleted,
      isCompleted,
      markUnCompleted,
      openAccordionId,
      firstFieldId,
      openMap,
      handleNext,
      getGroupItem,
    }),
    [openAccordionId, openMap],
  );

  return <GroupsFieldContext.Provider value={contextValue}>{children}</GroupsFieldContext.Provider>;
};

export const useGroupsField = (): TGroupsFieldContext => {
  const context = useContext(GroupsFieldContext);
  if (!context) {
    throw new Error('useGroupsField must be used within an GroupsFieldProvider');
  }
  return context;
};
